import React, { useMemo, useState } from 'react';
import API from '../ApiInterface';
import SDKAppForm from './SDKAppForm';
import { buildOverrides, createSDKApp, generateUuid, isSDKAppValid } from './sdkPartnerUtils';
import { codeBoxClass, inputClass, primaryButtonClass, secondaryButtonClass, sectionCardClass, shellClass } from './ui';

const RequiredMark = () => <span className="ml-1 text-rose-500">*</span>;

const OnboardSDKPartner = ({ token, featureSets, sdkFeatures, sdkDeviceFeatures, onClose }) => {
  const [partnerId] = useState(() => generateUuid());
  const [name, setName] = useState('');
  const [active, setActive] = useState(true);
  const [apps, setApps] = useState([createSDKApp()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const featureSetIds = useMemo(
    () => (Array.isArray(featureSets) ? featureSets.map((featureSet) => featureSet.featureSetId).filter(Boolean) : []),
    [featureSets]
  );

  const canSubmit = Boolean(name.trim()) && apps.length > 0 && apps.every(isSDKAppValid);

  const updateApp = (index, updater) => {
    setApps((prev) => prev.map((app, currentIndex) => (currentIndex === index ? updater(app) : app)));
  };

  const handleTextChange = (index, key, value) => {
    updateApp(index, (app) => ({ ...app, [key]: value }));
  };

  const handleBooleanChange = (index, key, value) => {
    updateApp(index, (app) => ({ ...app, [key]: value }));
  };

  const handleOverrideChange = (index, type, overrideIndex, key, value) => {
    updateApp(index, (app) => {
      const fieldKey = type === 'add' ? 'addOverrides' : 'removeOverrides';
      const nextOverrides = (app[fieldKey] || []).map((override, currentIndex) =>
        currentIndex === overrideIndex ? { ...override, [key]: value } : override
      );

      return { ...app, [fieldKey]: nextOverrides };
    });
  };

  const handleAddOverride = (index, type) => {
    updateApp(index, (app) => {
      const fieldKey = type === 'add' ? 'addOverrides' : 'removeOverrides';
      return {
        ...app,
        [fieldKey]: [...(app[fieldKey] || []), { sdkDeviceFeature: '', sdkFeature: '' }],
      };
    });
  };

  const handleAddApp = () => {
    setApps((prev) => [...prev, createSDKApp()]);
  };

  const handleSubmit = async () => {
    if (!canSubmit || !token) return;

    setSaving(true);
    setError('');

    const body = {
      id: partnerId,
      name: name.trim(),
      active,
      apps: apps.map((app) => {
        const payload = {
          apiKey: app.apiKey,
          bundleId: app.bundleId.trim(),
          featureSetId: app.featureSetId,
          enabled: app.enabled,
          collectMeasurements: app.collectMeasurements,
          collectTelemetry: app.collectTelemetry,
        };

        const overrides = buildOverrides(app);
        if (overrides) {
          payload.overrides = overrides;
        }

        return payload;
      }),
    };

    const response = await API.addSDKPartner(token, body);
    if (!response || response.status < 200 || response.status >= 300) {
      setError(response?.data?.errorDescription || response?.data?.message || 'Failed to add SDK partner.');
      setSaving(false);
      return;
    }

    setSaving(false);
    if (typeof onClose === 'function') {
      await onClose();
    }
  };

  return (
    <div className={shellClass}>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h3 className="mt-1 text-lg font-semibold text-slate-900">Onboard SDK Partner</h3>
        </div>
        <button type="button" onClick={handleAddApp} className={secondaryButtonClass}>
          + App
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className={`${sectionCardClass} mb-4`}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">ID</label>
            <div className={codeBoxClass}>
              {partnerId}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Partner Name<RequiredMark /></label>
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className={inputClass}
            />
          </div>

          <div className="flex items-center gap-2 pt-1 md:pt-7">
            <input
              id="sdk-partner-active"
              type="checkbox"
              checked={active}
              onChange={(event) => setActive(event.target.checked)}
            />
            <label className="text-sm text-slate-700" htmlFor="sdk-partner-active">
              Active
            </label>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {apps.map((app, index) => (
          <SDKAppForm
            key={app.apiKey}
            app={app}
            index={index}
            featureSetIds={featureSetIds}
            sdkFeatures={sdkFeatures}
            sdkDeviceFeatures={sdkDeviceFeatures}
            onTextChange={handleTextChange}
            onBooleanChange={handleBooleanChange}
            onOverrideChange={handleOverrideChange}
            onAddOverride={handleAddOverride}
          />
        ))}
      </div>

      <div className="mt-6 flex gap-2">
        <button type="button" onClick={onClose} disabled={saving} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="button" onClick={handleSubmit} disabled={!canSubmit || saving} className={primaryButtonClass}>
          {saving ? 'Adding...' : 'Add SDK Partner'}
        </button>
      </div>
    </div>
  );
};

export default OnboardSDKPartner;
